'use client';

import * as React from 'react';
import {
  WorkflowProvider,
  WorkflowContent,
  WorkflowNavigation,
  type WorkflowStep,
  useWorkflow
} from '@/components/ui/workflow';
import { SendNotificationsStep } from './steps/send-notifications-step';
import { useNotificationWorkflow } from '@/lib/services/notifications/workflow-hooks';
import { NotificationsWorkflowSkeleton } from './notifications-workflow-skeleton';
import { AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from '@/lib/toast';
import type { NotificationsWorkflowProps } from './types';
import type {
  WorkflowInstitution,
  WorkflowDocument,
  BackendCorrespondence
} from '@/lib/services/correspondences/workflows/workflow-types';
import type { LawFirmUser } from '@/lib/services/account/types';
import WorkflowDataService from '@/lib/services/correspondences/workflows/workflow-data.service';

// Constants for step indices
const WORKFLOW_STEPS = {
  SELECT_INSTITUTIONS: 0,
  SEND_NOTIFICATIONS: 1
} as const;

// Inner component with workflow context access
function WorkflowNavigationWithBulkCreate({
  matterId,
  workflowData,
  refetchWorkflowData,
  allSentSuccessfully,
  isSingleStep,
  onComplete
}: {
  matterId: number;
  workflowData: {
    institutions?: WorkflowInstitution[];
    documents?: WorkflowDocument[];
    signatories?: LawFirmUser[];
    correspondences?: BackendCorrespondence[];
  };
  refetchWorkflowData: () => Promise<unknown>;
  allSentSuccessfully: boolean;
  isSingleStep?: boolean;
  onComplete: (data: Record<string, unknown>) => void;
}) {
  const { currentStepIndex, nextStep, getStepData } = useWorkflow();
  const [isCreatingDrafts, setIsCreatingDrafts] = React.useState(false);
  const [isNextDisabled, setIsNextDisabled] = React.useState(false);

  const handleNext = async () => {
    // Prevent multiple clicks
    if (isNextDisabled || isCreatingDrafts) return;

    // When moving from Select Institutions to Send Notifications
    if (currentStepIndex === WORKFLOW_STEPS.SELECT_INSTITUTIONS) {
      const institutions =
        getStepData('institutions') || workflowData?.institutions || [];
      const selectedInstitutions = institutions.filter(
        (inst: WorkflowInstitution & { selected?: boolean }) => inst.selected
      );

      if (selectedInstitutions.length > 0) {
        setIsCreatingDrafts(true);
        setIsNextDisabled(true);

        try {
          // Create bulk drafts for all selected institutions
          const institutionIds = selectedInstitutions.map(
            (inst: WorkflowInstitution) => inst.id
          );

          // Use server action instead of client-side API call
          const result = await WorkflowDataService.createBulkDraftsAction(
            matterId,
            'notification',
            institutionIds
          );
          if (!result.success) {
            throw new Error(result.error || 'Failed to create drafts');
          }

          // Refresh workflow data to get the new correspondences
          try {
            await refetchWorkflowData();
          } catch (refetchError) {
            console.error('Failed to refresh workflow data:', refetchError);
            // Continue anyway as drafts were created successfully
          }

          // Move to next step
          nextStep();
        } catch (error) {
          console.error('Failed to create bulk drafts:', error);
          toast.error('Failed to prepare notifications. Please try again.');
        } finally {
          setIsCreatingDrafts(false);
          setIsNextDisabled(false);
        }
      } else {
        // No institutions selected, just move to next step (will show error)
        nextStep();
      }
    } else {
      // For other steps, just navigate normally
      nextStep();
    }
  };

  if (isCreatingDrafts) {
    return (
      <div className="flex items-center justify-center py-4">
        <div className="space-y-2 text-center">
          <div className="border-primary mx-auto h-6 w-6 animate-spin rounded-full border-2 border-t-transparent" />
          <p className="text-muted-foreground text-sm">
            Preparing correspondence...
          </p>
        </div>
      </div>
    );
  }

  return (
    <WorkflowNavigation
      onNext={handleNext}
      submitLabel="Return to Matter"
      onSubmit={() => onComplete({})}
      disableBack={allSentSuccessfully}
      hideFooterNav={isSingleStep}
    />
  );
}

export function NotificationsWorkflow({
  matterId,
  onComplete
}: NotificationsWorkflowProps) {
  const {
    data,
    isLoading,
    error,
    validation,
    sendNotifications,
    refetchWorkflowData,
    isSending,
    sendError: _sendError,
    sendResults
  } = useNotificationWorkflow(matterId);

  // Always use real data, never mock data
  const workflowData = React.useMemo(() => {
    return (
      data || {
        institutions: [],
        documents: [],
        signatories: []
      }
    );
  }, [data]);

  // Track if all correspondence has been sent successfully
  const allSentSuccessfully = React.useMemo(() => {
    if (!sendResults) return false;
    return sendResults.failed.length === 0 && sendResults.successful.length > 0;
  }, [sendResults]);

  // Validate required props (after all hooks have been called).
  if (!matterId) {
    return (
      <div className="container mx-auto max-w-6xl p-6">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Missing required matter ID. Please ensure the matter is properly
            configured.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const steps: WorkflowStep[] = [
    {
      id: 'send-notifications',
      title: 'Send Correspondence',
      description: 'Review and send notifications to selected institutions',
      component: (
        <SendNotificationsStep
          initialData={workflowData}
          onSend={sendNotifications}
          onRefreshCorrespondences={async () => {
            await refetchWorkflowData();
          }}
          isSending={isSending}
          sendResults={sendResults}
          matterId={matterId}
        />
      )
    }
  ];

  const handleComplete = (data: Record<string, unknown>) => {
    onComplete?.(data);
  };

  // Show loading state with skeleton
  if (isLoading) {
    return <NotificationsWorkflowSkeleton />;
  }

  // Show error state
  if (error) {
    return (
      <div className="container mx-auto max-w-6xl p-6">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Failed to load workflow data:{' '}
            {error instanceof Error ? error.message : 'Unknown error'}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  // Check if we have institutions to notify
  const hasInstitutions = workflowData?.institutions?.length > 0;

  if (!isLoading && !hasInstitutions) {
    return (
      <div className="container mx-auto max-w-6xl p-6">
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <div className="space-y-2">
              <div className="font-medium">
                No institutions ready for notification
              </div>
              <p className="text-sm">
                Please add institutions to the matter's inventory first, then
                return here to send notifications.
              </p>
            </div>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <WorkflowProvider steps={steps} onComplete={handleComplete}>
      <div className="container mx-auto flex h-full max-w-6xl flex-col space-y-8 p-6">
        {/* Show blocking errors if any */}
        {validation.blockingErrors.length > 0 && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <div className="space-y-1">
                <div className="font-medium">
                  The following issues must be resolved:
                </div>
                <ul className="list-inside list-disc text-sm">
                  {validation.blockingErrors.map((error) => (
                    <li key={error}>{error}</li>
                  ))}
                </ul>
              </div>
            </AlertDescription>
          </Alert>
        )}

        <WorkflowContent />
        <WorkflowNavigationWithBulkCreate
          matterId={matterId}
          workflowData={workflowData}
          refetchWorkflowData={refetchWorkflowData}
          allSentSuccessfully={allSentSuccessfully}
          onComplete={handleComplete}
          isSingleStep={steps.length === 1}
        />
      </div>
    </WorkflowProvider>
  );
}
